import { getAuthCredentials } from "@utils/auth-utils";
import { ROUTES } from "@utils/routes";
import axios, { AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import Router from "next/router";
import * as Sentry from "@sentry/nextjs";

// Extend AxiosRequestConfig to include metadata with sentryTransaction
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  metadata?: {
    sentryTransaction?: any; // Using any type to avoid type reference issues
    [key: string]: any;
  };
}

const http = axios.create({
  baseURL: process.env.NEXT_PUBLIC_REST_API_ENDPOINT, // TODO: take this api URL from env
  timeout: 30000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Change request data/error here
http.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    const { token } = getAuthCredentials();
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };

    // Start a Sentry transaction for API request
    const transaction = Sentry.startTransaction({
      name: `${config.method?.toUpperCase() || "UNKNOWN"} ${
        config.url || "unknown-url"
      }`,
      op: "http.client",
    });

    // Store transaction in request config to be used in response interceptor
    config.metadata = { ...config.metadata, sentryTransaction: transaction };

    return config;
  },
  (error) => {
    // Capture request errors
    Sentry.captureException(error, {
      tags: {
        type: "request_error",
      },
    });
    return Promise.reject(error);
  }
);

// Change response data/error here
http.interceptors.response.use(
  (response) => {
    // Finish the Sentry transaction for successful API response
    const config = response.config as ExtendedAxiosRequestConfig;
    const transaction = config.metadata?.sentryTransaction;
    if (transaction) {
      transaction.setTag("http.status_code", response.status);
      transaction.setTag(
        "http.method",
        config.method?.toUpperCase() || "UNKNOWN"
      );
      transaction.setTag("http.url", config.url || "unknown-url");
      transaction.finish();
    }

    return response;
  },
  (error) => {
    // Capture API error details in Sentry
    const errorResponse = error.response;
    const config = error.config as ExtendedAxiosRequestConfig;

    // Finish the Sentry transaction for failed API response
    const transaction = config?.metadata?.sentryTransaction;
    if (transaction) {
      transaction.setTag("http.status_code", errorResponse?.status);
      transaction.setTag(
        "http.method",
        config?.method?.toUpperCase() || "UNKNOWN"
      );
      transaction.setTag("http.url", config?.url || "unknown-url");
      transaction.setStatus("error");
      transaction.finish();
    }

    // Capture exception in Sentry with added context
    Sentry.captureException(error, {
      tags: {
        type: "api_error",
        status: errorResponse?.status,
        statusText: errorResponse?.statusText,
      },
      extra: {
        url: config?.url,
        method: config?.method,
        data: config?.data,
        response: {
          data: errorResponse?.data,
          status: errorResponse?.status,
          statusText: errorResponse?.statusText,
        },
      },
    });

    if (
      (errorResponse && errorResponse.status === 401) ||
      (errorResponse && errorResponse.status === 403) ||
      (errorResponse && errorResponse.data.message === "SSTNG_ERROR.NOT_AUTHORIZED")
    ) {
      Cookies.remove("AUTH_CRED");
      Router.push(ROUTES.LOGIN);
    }
    return Promise.reject(error);
  }
);

export default http;
